import clsx from 'clsx'
import noop from 'lodash/noop'
import React, { ComponentProps, CSSProperties, ReactNode, useRef, useState } from 'react'

import { NavLinkClickProperties } from '@Analytics/ampli'
import SVGAngleLeft from '@Assets/SVG/AngleLeft'
import SVGAngleRight from '@Assets/SVG/AngleRight'
import { Background } from '@Components/Background'
import { VimeoPlayer } from '@Components/CMS/Video/VimeoPlayer'
import { Container } from '@Components/Container'
import { ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { HTMLSliderElement, Slider } from '@Components/Slider'
import styles from '@Components/Slider/Carousel/Showcase.module.scss'
import { ReferenceContentFragment } from '@Generated/graphql'

export const ShowcaseCarouselSlider = ({
  children,
  ...props
}: ComponentProps<typeof Container>) => {
  const sliderRef = useRef<HTMLSliderElement | null>(null)
  const [sliderState, setSliderState] = useState<{
    max: number
    min: number
    value: number
  } | null>(null)

  return (
    <Container {...props}>
      <div className={styles.sliderWithActions}>
        <div className={styles.actions}>
          <input
            className={clsx(styles.range, !sliderState && styles.hidden)}
            max={sliderState?.max ?? 0}
            min={sliderState?.min ?? 0}
            onChange={noop}
            type="range"
            value={sliderState?.value ?? 0}
          />
          <button className={clsx('arrow', !sliderState && styles.hidden)} onClick={() => sliderRef.current?.prev()} type="button">
            {' '}
            <SVGAngleLeft />
          </button>
          <button className={clsx('arrow', !sliderState && styles.hidden)} onClick={() => sliderRef.current?.next()} type="button">
            {' '}
            <SVGAngleRight />
          </button>
        </div>
        <Slider
          className={clsx(styles.slider, 'slider')}
          onSlide={(e) => {
            if (!e.isScrollable) {
              setSliderState(null)
              return
            }
            setSliderState({
              max: e.rangeMax,
              min: e.rangeMin,
              value: e.rangeValue,
            })
          }}
          ref={sliderRef}
        >
          {children}
        </Slider>
      </div>
    </Container>
  )
}

export const ShowcaseCarouselSliderItem = ({
  analytics,
  background,
  backgroundColor,
  children,
  href,
  onClick,
  target,
  textColor,
  variant = 'spot',
}: {
  background?: ReferenceContentFragment
  backgroundColor?: string
  children?: ReactNode
  textColor?: string
  variant?: 'background' | 'background-without-text' | 'spot'
} & (
  | {
    analytics?: NavLinkClickProperties
    href: string
    onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
    target?: string
  }
  | {
    analytics?: never
    href?: never
    onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
    target?: never
  }
)) => {
  const style = {
    '--background-color': backgroundColor,
    '--text-color': textColor,
  } as CSSProperties

  const content = (
    <>
      {(variant === 'background' || variant === 'background-without-text') && (
        <Background asset={background} className={styles.background} priority sizes="200px" />
      )}
      {variant === 'spot' && (
        <>
          {
            (
              background?.__typename === 'ImageAsset'
              || background?.__typename === 'ImageResource'
              || background?.__typename === 'Link'
            )
            && background?.image?.url
            && (
              <ImageV4
                alt=""
                className={styles.image}
                draggable={false}
                src={background.image.url}
                width={100}
              />
            )
          }
          {background?.__typename === 'VideoResource' && background?.asset?.type === 'vimeo' && background?.asset?.key && (
            <VimeoPlayer
              className={styles.video}
              enableControls={false}
              options={{
                autoplay: true,
                muted: true,
                loop: true,
              }}
              videoID={background.asset.key}
            />
          )}
        </>
      )}

      <div className={styles.content}>
        {children}
      </div>
    </>
  )

  if (typeof href === 'string') {
    return (
      <Link
        analytics={analytics}
        className={clsx(
          styles.item,
          variant === 'background' && styles.variantBackground,
          variant === 'background-without-text' && styles.variantBackgroundWithoutText,
          variant === 'spot' && styles.variantSpot,
        )}
        href={href}
        onClick={onClick}
        style={style}
        target={target}
      >
        {content}
      </Link>
    )
  }

  return (
    <div
      aria-current={onClick ? 'page' : undefined}
      className={clsx(
        styles.item,
        variant === 'background' && styles.variantBackground,
        variant === 'background-without-text' && styles.variantBackgroundWithoutText,
        variant === 'spot' && styles.variantSpot,
      )}
      onClick={onClick}
      role={onClick ? 'link' : undefined}
      style={style}
    >
      {content}
    </div>
  )
}
